import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import Timeline from './components/Timeline';
import VideoTimeline from './components/VideoTimeline';
import ClipEditModal from './components/ClipEditModal';
import AddLanguageModal from './components/AddLanguageModal';
import VoiceModelSelectionModal from './components/VoiceModelSelectionModal';
import ConfirmationDialog from './components/ConfirmationDialog'; // Import ConfirmationDialog
import BulkPrepareModal from './components/BulkPrepareModal';
import BulkProgressModal from './components/BulkProgressModal';
import './App.css';

import { useProject } from './hooks/useProject';
import { useTimeline } from './hooks/useTimeline';
import { useVideo } from './hooks/useVideo';
import { useTimelineActions } from './hooks/useTimelineActions';
import { useVideoActions } from './hooks/useVideoActions';
import { useAI } from './hooks/useAI';
import { useClipEditing } from './hooks/useClipEditing';
import { useVideoPlayback } from './hooks/useVideoPlayback';
import { API_URL } from './Config';

function VideoEditorPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' }); // type: 'success' or 'error'
  const [selectedLanguage, setSelectedLanguage] = useState(localStorage.getItem('selectedLanguage') || 'original');
  const [newTranslationLanguage, setNewTranslationLanguage] = useState('');
  
  const [isAddLanguageModalOpen, setIsAddLanguageModalOpen] = useState(false);
  const [isVoiceModelModalOpen, setIsVoiceModelModalOpen] = useState(false);
  const [isConfirmRemoveOpen, setIsConfirmRemoveOpen] = useState(false);
  const [isBulkPrepareModalOpen, setIsBulkPrepareModalOpen] = useState(false);
  const [isBulkProgressModalOpen, setIsBulkProgressModalOpen] = useState(false);
  const [regeneratingItemId, setRegeneratingItemId] = useState(null); // New state for single item regeneration
  const [regeneratingVariation, setRegeneratingVariation] = useState(1); // New state for variation

  // Multi-video support state
  const [videos, setVideos] = useState([]);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);

  // Video menu dropdown state
  const [isVideoMenuOpen, setIsVideoMenuOpen] = useState(false);

  // Check if running on localhost to hide certain buttons
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  // Handler for navigating to published videos page
  const handleOpenPublishedVideos = () => {
    navigate(`/project/${projectId}/published-videos`);
  };

  const languageNames = {
    es: 'Spanish',
    fr: 'French',
    de: 'German',
    zh: 'Chinese (Simplified)',
    ja: 'Japanese',
    ar: 'Arabic',
    hi: 'Hindi',
    pt: 'Portuguese',
    ru: 'Russian',
    it: 'Italian',
    ko: 'Korean',
    nl: 'Dutch',
    sv: 'Swedish',
    tr: 'Turkish',
    ta: 'Tamil',
    te: 'Telugu',
    id: 'Indonesian',
    // Add more languages as needed
  };

  // Helper function to get language display text
  const getLanguageDisplayText = (langCode) => {
    if (langCode === 'original') return 'English';
    return languageNames[langCode] || langCode.toUpperCase();
  };

  const { project, videoUrl, setVideoUrl, timelineItems, setTimelineItems, fetchProjectAndTimeline, updateProject, availableLanguages } = useProject(projectId);
  const { updateTimelineOrder, handleUpdateText, handleUpdateClipTimes, handleUpdateSceneDescription, handleUpdateSceneNarration } = useTimeline(projectId, timelineItems, setTimelineItems, setIsLoading, setMessage);

  // Check if any timeline items have descriptions
  const hasDescriptions = timelineItems.some(item => item.rawText && item.rawText.trim() !== '');
  
  // Check if any timeline items already have audio/narration (excluding intro/outro clips)
  const hasExistingNarrations = timelineItems.some(item => {
    // Skip intro/outro clips as they use original video audio
    if (item.type === 'intro' || item.type === 'outro') {
      return false;
    }
    
    if (selectedLanguage === 'original') {
      return item.scene_audio; // Check for scene_audio in original language
    } else {
      // Check for translated audio path in selected language
      return item.translations && item.translations.find(t => t.language_code === selectedLanguage)?.translated_audio_path;
    }
  });

  // Check if any timeline items have empty narration scripts (excluding intro/outro clips)
  const hasEmptyNarrations = timelineItems.some(item => {
    // Skip intro/outro clips as they don't need AI-generated narration
    if (item.type === 'intro' || item.type === 'outro') {
      return false;
    }
    
    if (selectedLanguage === 'original') {
      return !item.scene_narration || item.scene_narration.trim() === '';
    } else {
      const translation = item.translations && item.translations.find(t => t.language_code === selectedLanguage);
      return !translation || !translation.translated_text || translation.translated_text.trim() === '';
    }
  });
  const { videoRef, currentTime, duration, isPlaying, handleFileChange, handleResetVideo, handleProgress, handleLoadedMetadata, handleSeek, setIsPlaying } = useVideo(projectId, videoUrl, setVideoUrl, updateProject);
  const { addFrame, addClip, handleRemoveItem, handleDragEnd, handleMoveUp, handleMoveDown, handleDuplicateItem } = useTimelineActions(projectId, timelineItems, setTimelineItems, updateTimelineOrder, currentVideoIndex, videos);
  const { handleAddFrameToTimeline, handleAddClipToTimeline, isRecordingClip } = useVideoActions(videoRef, addFrame, addClip, setIsPlaying);
  

  const { handleAIGenScript, handleAIGenVoice, handleRegenScript, handleRemoveAudio } = useAI(projectId, setTimelineItems, setIsLoading, setMessage, selectedLanguage);
  const { isClipEditModalOpen, setIsClipEditModalOpen, editingClip, handleEditClip } = useClipEditing();

  // Function to get the correct video URL for a specific clip based on its video_index
  const getClipVideoUrl = useCallback((clip) => {
    if (!clip || !videos.length) {
      console.log('🎬 ClipEditModal: Using current video (no clip or videos):', videoUrl);
      return videoUrl;
    }

    const clipVideoIndex = clip.video_index;
    if (typeof clipVideoIndex === 'number' && clipVideoIndex >= 0 && clipVideoIndex < videos.length) {
      const clipVideo = videos[clipVideoIndex];
      const finalUrl = clipVideo.startsWith('http') ? clipVideo : `${API_URL}${clipVideo}`;
      console.log(`🎬 ClipEditModal: Using video at index ${clipVideoIndex} for clip ${clip.id}:`, finalUrl);
      return finalUrl;
    }

    // Fallback to current video
    console.log(`🎬 ClipEditModal: Using current video (fallback) for clip ${clip.id}:`, videoUrl);
    return videoUrl;
  }, [videos, videoUrl]);

  const handleRegenerateSingleAudio = (itemId, variation = 1) => {
    setRegeneratingItemId(itemId); // Set the item ID
    setRegeneratingVariation(variation); // Set the variation
    setIsVoiceModelModalOpen(true); // Open the modal
  };


  const handleQuickPreview = (items) => {
    // Navigate to Quick Preview page
    navigate(`/project/${projectId}/quick-preview`);
  };


  useEffect(() => {
    localStorage.setItem('selectedLanguage', selectedLanguage);
  }, [selectedLanguage]);

  // Initialize videos array when project loads
  useEffect(() => {
    if (project && project.video_url) {
      console.log('🏗️ Initializing videos from project:');
      console.log('  project.video_url:', project.video_url);
      console.log('  project.videoUrl:', project.videoUrl);

      try {
        // Check if video_url is an array (JSON string) or single URL
        const parsed = JSON.parse(project.video_url);
        console.log('  Parsed as JSON:', parsed);

        if (Array.isArray(parsed)) {
          setVideos(parsed);
          if (parsed.length > 0) {
            // Ensure the video URL is a full URL
            const fullUrl = parsed[0].startsWith('http') ? parsed[0] : `${API_URL}${parsed[0]}`;
            console.log('  Setting videoUrl to:', fullUrl);
            setVideoUrl(fullUrl);
            setCurrentVideoIndex(0);
          }
        } else {
          // Single URL case - convert to array format
          const videoArray = [project.video_url];
          console.log('  Single URL, creating array:', videoArray);
          setVideos(videoArray);
          const fullUrl = project.video_url.startsWith('http') ? project.video_url : `${API_URL}${project.video_url}`;
          console.log('  Setting videoUrl to:', fullUrl);
          setVideoUrl(fullUrl);
          setCurrentVideoIndex(0);
        }
      } catch (e) {
        // Not valid JSON, treat as single URL (backward compatibility)
        console.log('  Not valid JSON, treating as single URL');
        if (project.video_url) {
          const videoArray = [project.video_url];
          console.log('  Creating array from single URL:', videoArray);
          setVideos(videoArray);
          const fullUrl = project.video_url.startsWith('http') ? project.video_url : `${API_URL}${project.video_url}`;
          console.log('  Setting videoUrl to:', fullUrl);
          setVideoUrl(fullUrl);
          setCurrentVideoIndex(0);
        }
      }
    }
  }, [project]);

  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ text: '', type: '' });
      }, 3000); // 3 seconds
      return () => clearTimeout(timer);
    }
  }, [message]);

  const handleTranslate = async () => {
    if (!newTranslationLanguage) {
      setMessage({ text: "Please select a language to translate to.", type: "error" });
      return;
    }
    setIsLoading(true);
    setMessage({ text: '', type: '' });
    try {
      const response = await fetch(`${API_URL}/projects/${projectId}/translate?target_language_code=${newTranslationLanguage}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }
      await response.json();
      setMessage({ text: `Translation to ${newTranslationLanguage} complete!`, type: 'success' });
      await fetchProjectAndTimeline();
      setSelectedLanguage(newTranslationLanguage);
    } catch (error) {
      setMessage({ text: `Failed to translate: ${error.message}`, type: 'error' });
      console.error("Error translating project:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveTranslation = async () => {
    setIsConfirmRemoveOpen(false);
    if (selectedLanguage === 'original') {
      setMessage({ text: "Cannot remove the original language.", type: "error" });
      return;
    }
    setIsLoading(true);
    setMessage({ text: '', type: '' });
    try {
      const response = await fetch(`${API_URL}/projects/${projectId}/translations/${selectedLanguage}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      setMessage({ text: result.message, type: 'success' });
      setSelectedLanguage('original'); // Switch back to original
      await fetchProjectAndTimeline(); // Refresh data
    } catch (error) {
      setMessage({ text: `Failed to remove translation: ${error.message}`, type: 'error' });
      console.error("Error removing translation:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddIntroOutro = async () => {
    setIsLoading(true);
    setMessage({ text: '', type: '' });
    try {
      const response = await fetch(`${API_URL}/projects/${projectId}/add-intro-outro`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      setMessage({ text: result.message, type: 'success' });
      await fetchProjectAndTimeline(); // Refresh timeline data
    } catch (error) {
      setMessage({ text: `Failed to add intro/outro: ${error.message}`, type: 'error' });
      console.error("Error adding intro/outro:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChangeAndSetUrl = async (event) => {
    const newVideoUrl = await handleFileChange(event);
    if (newVideoUrl) {
      setVideoUrl(`${API_URL}${newVideoUrl}`);
    }
  };

  const handleAddVideo = () => {
    // Create file input element for adding video
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'video/*';
    input.onchange = async (event) => {
      const newVideoUrl = await handleFileChange(event);
      if (newVideoUrl) {
        // Store relative path in database for consistency
        const relativePath = newVideoUrl;
        const fullVideoUrl = `${API_URL}${relativePath}`;

        // Add to videos array (store relative paths for consistency)
        let updatedVideos;
        if (videos.length === 0) {
          // First time adding a video - get current playing video
          const currentRelativePath = videoUrl ? videoUrl.replace(API_URL, '') : relativePath;
          console.log('🎬 Adding first additional video:');
          console.log('  Current videoUrl:', videoUrl);
          console.log('  Current relativePath:', currentRelativePath);
          console.log('  New relativePath:', relativePath);
          console.log('  Videos array before:', videos);
          updatedVideos = [currentRelativePath, relativePath];
          console.log('  Updated videos array:', updatedVideos);
        } else {
          updatedVideos = [...videos, relativePath];
        }

        setVideos(updatedVideos);

        // Update the project video_url field to store array
        await updateProjectVideos(updatedVideos);

        // Switch to the newly added video
        setCurrentVideoIndex(updatedVideos.length - 1);
        setVideoUrl(fullVideoUrl);
      }
    };
    input.click();
  };

  const updateProjectVideos = async (videoArray) => {
    try {
      const response = await fetch(`${API_URL}/projects/${projectId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          video_url: JSON.stringify(videoArray)
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error updating project videos:', error);
      setMessage({ text: 'Failed to update project videos', type: 'error' });
    }
  };

  const handleVideoToggle = (direction) => {
    const newIndex = direction === 'next'
      ? (currentVideoIndex + 1) % videos.length
      : (currentVideoIndex - 1 + videos.length) % videos.length;

    console.log('🔄 Video toggle:');
    console.log('  Direction:', direction);
    console.log('  Current index:', currentVideoIndex);
    console.log('  New index:', newIndex);
    console.log('  Videos array:', videos);
    console.log('  Selected video:', videos[newIndex]);

    setCurrentVideoIndex(newIndex);

    // Ensure the video URL is a full URL
    const selectedVideo = videos[newIndex];
    const fullUrl = selectedVideo && selectedVideo.startsWith('http')
      ? selectedVideo
      : `${API_URL}${selectedVideo}`;

    console.log('  Full URL:', fullUrl);
    setVideoUrl(fullUrl);
  };

  const handleReplaceVideo = () => {
    // Create file input element for replacing current video
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'video/*';
    input.onchange = async (event) => {
      const file = event.target.files[0];
      if (!file) return;

      try {
        // Get the current video path to determine the target filename
        const currentVideoPath = videos[currentVideoIndex] || '';
        const targetFilename = currentVideoPath.split('/').pop(); // Extract filename from path

        if (!targetFilename) {
          alert('Cannot determine current video filename');
          return;
        }

        // Create FormData with the target filename to replace the existing file
        const formData = new FormData();
        formData.append('video', file);
        formData.append('target_filename', targetFilename); // Tell backend to use this filename

        const response = await fetch(`${API_URL}/projects/${projectId}/replace-video`, {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Force video to reload by updating the src with a cache-busting parameter
        const timestamp = new Date().getTime();
        const currentVideoUrl = videos[currentVideoIndex];
        const fullVideoUrl = currentVideoUrl.startsWith('http')
          ? `${currentVideoUrl}?t=${timestamp}`
          : `${API_URL}${currentVideoUrl}?t=${timestamp}`;

        setVideoUrl(fullVideoUrl);
        console.log('🔄 Replaced video file:', targetFilename, 'with cache-bust:', timestamp);

        // Show success message
        setMessage({ text: 'Video replaced successfully!', type: 'success' });

      } catch (error) {
        console.error('Error replacing video:', error);
        setMessage({ text: `Failed to replace video: ${error.message}`, type: 'error' });
      }
    };
    input.click();
    setIsVideoMenuOpen(false);
  };

  const handleGenerateTutorial = async () => {
    if (!projectId) return;
    
    setIsLoading(true);
    setMessage({ text: '', type: '' });
    
    try {
      // First check if tutorial already exists
      const checkResponse = await fetch(`${API_URL}/projects/${projectId}/tutorial`);
      
      if (checkResponse.ok) {
        // Tutorial exists, navigate to it
        navigate(`/project/${projectId}/tutorial`);
      } else if (checkResponse.status === 404) {
        // Tutorial doesn't exist, generate it
        const generateResponse = await fetch(`${API_URL}/projects/${projectId}/generate-tutorial`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        
        if (!generateResponse.ok) {
          const errorData = await generateResponse.json();
          throw new Error(errorData.detail || `HTTP error! status: ${generateResponse.status}`);
        }
        
        const result = await generateResponse.json();
        setMessage({ text: 'Tutorial generated successfully!', type: 'success' });
        
        // Navigate to the generated tutorial
        navigate(`/project/${projectId}/tutorial`);
      } else {
        throw new Error(`Failed to check tutorial status: ${checkResponse.status}`);
      }
    } catch (error) {
      setMessage({ text: `Failed to generate tutorial: ${error.message}`, type: 'error' });
      console.error("Error generating tutorial:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Bulk Prepare functionality
  const handleBulkPrepare = async (selectedLanguages) => {
    console.log('🚀 [DEBUG] handleBulkPrepare started with languages:', selectedLanguages);
    console.log('🚀 [DEBUG] Project ID:', projectId);
    console.log('🚀 [DEBUG] Project object:', project);

    if (!project?.id || selectedLanguages.length === 0) {
      console.log('❌ [DEBUG] Early return - missing project or languages');
      return;
    }

    console.log('✅ [DEBUG] Validation passed, proceeding with bulk prepare');

    // Hide language selection modal and show progress modal
    console.log('🔄 [DEBUG] Switching modals...');
    setIsBulkPrepareModalOpen(false);
    setIsBulkProgressModalOpen(true);
    console.log('✅ [DEBUG] Modals switched');

    // Initialize progress tracking in localStorage
    const progressKey = `bulkPrepare_${projectId}`;
    const progressData = {
      projectId,
      languages: selectedLanguages,
      currentIndex: 0,
      completedLanguages: [],
      startTime: Date.now(),
      status: 'processing'
    };
    localStorage.setItem(progressKey, JSON.stringify(progressData));
    console.log('✅ [DEBUG] Progress data stored:', progressData);

    // Start processing languages sequentially in the background
    console.log('🔄 [DEBUG] Starting sequential processing...');
    processBulkPrepareSequentially(selectedLanguages, progressKey);
  };

  const processBulkPrepareSequentially = async (languages, progressKey) => {
    for (let i = 0; i < languages.length; i++) {
      const language = languages[i];

      // Update progress
      const progressData = JSON.parse(localStorage.getItem(progressKey) || '{}');
      progressData.currentIndex = i;
      progressData.currentLanguage = language;
      progressData.status = 'processing';
      progressData.currentStep = 'adding_language';
      localStorage.setItem(progressKey, JSON.stringify(progressData));

      console.log(`🚀 Bulk Prepare: Starting ${language} (${i + 1}/${languages.length})`);

      try {
        // Step 1: Add/create the language first
        console.log(`📝 Bulk Prepare: Adding language ${language} to project`);
        progressData.currentStep = 'adding_language';
        localStorage.setItem(progressKey, JSON.stringify(progressData));

        const languageAdded = await addLanguageToProject(language);

        if (!languageAdded) {
          console.error(`❌ Bulk Prepare: Failed to add language ${language}, skipping`);
          continue;
        }

        console.log(`✅ Bulk Prepare: Language ${language} added successfully`);

        // Step 2: Generate AI scripts for this language
        console.log(`🤖 [BULK] Step 2: Starting AI script generation for ${language}`);
        progressData.currentStep = 'generating_scripts';
        localStorage.setItem(progressKey, JSON.stringify(progressData));

        const scriptSuccess = await generateAIScriptForLanguage(language);
        console.log(`🤖 [BULK] Script generation result for ${language}:`, scriptSuccess);

        if (!scriptSuccess) {
          console.log(`⚠️ [BULK] Script generation failed for ${language}, starting polling process`);
          progressData.currentStep = 'polling';
          localStorage.setItem(progressKey, JSON.stringify(progressData));

          // Start polling for this language
          console.log(`⏳ [BULK] Starting polling for ${language} script completion...`);
          const pollSuccess = await pollForLanguageCompletion(language, progressKey);
          console.log(`⏳ [BULK] Polling completed for ${language}:`, pollSuccess);
        } else {
          console.log(`✅ [BULK] Script generation succeeded for ${language}, proceeding to voice generation`);
        }

        // Refresh timeline data to get the latest translations before voice generation
        console.log(`🔄 [BULK] Refreshing timeline data to get latest ${language} translations...`);
        await fetchProjectAndTimeline();
        console.log(`✅ [BULK] Timeline data refreshed, proceeding to voice generation`);

        // Step 3: Generate voice over for this language
        console.log(`🎤 [BULK] Step 3: Starting voice generation for ${language}`);
        console.log(`🎤 [BULK] Current progress before voice gen:`, progressData);
        progressData.currentStep = 'generating_voices';
        localStorage.setItem(progressKey, JSON.stringify(progressData));
        console.log(`🎤 [BULK] Progress updated, calling generateVoiceForLanguage(${language})`);

        const voiceSuccess = await generateVoiceForLanguage(language);
        console.log(`🎤 [BULK] Voice generation result for ${language}:`, voiceSuccess);

        if (voiceSuccess) {
          console.log(`✅ [BULK] Voice generation completed successfully for ${language}`);
        } else {
          console.log(`⚠️ [BULK] Voice generation failed for ${language}, but continuing`);
        }

        // Step 4: Generate configurations for this language
        console.log(`⚙️ [BULK] Step 4: Starting configuration generation for ${language}`);
        progressData.currentStep = 'generating_configurations';
        localStorage.setItem(progressKey, JSON.stringify(progressData));

        const configSuccess = await generateConfigurationsForLanguage(language);

        if (configSuccess) {
          console.log(`✅ [BULK] Configuration generation completed successfully for ${language}`);
        } else {
          console.log(`⚠️ [BULK] Configuration generation failed for ${language}, but continuing`);
        }

        // Mark language as completed
        progressData.completedLanguages.push(language);
        console.log(`🎤 [BULK] Updated completedLanguages:`, progressData.completedLanguages);

        // Add 2-second delay between languages (except for the last one)
        if (i < languages.length - 1) {
          console.log(`⏳ Bulk Prepare: Waiting 2 seconds before next language...`);
          await new Promise(resolve => setTimeout(resolve, 2000));
        }

      } catch (error) {
        console.error(`❌ Bulk Prepare: Error processing ${language}:`, error);
        // Continue to next language even if one fails
      }

      // Update progress after completion
      progressData.currentIndex = i + 1;
      progressData.currentStep = 'completed';
      localStorage.setItem(progressKey, JSON.stringify(progressData));
    }

    // Mark as complete
    const finalProgress = JSON.parse(localStorage.getItem(progressKey) || '{}');
    finalProgress.status = 'completed';
    finalProgress.endTime = Date.now();
    localStorage.setItem(progressKey, JSON.stringify(finalProgress));

    console.log('🎉 Bulk Prepare: All languages processed!');
    setMessage({ text: 'Bulk Prepare completed for all selected languages!', type: 'success' });

    // Refresh project data to show new languages
    await fetchProjectAndTimeline();
  };

  const addLanguageToProject = async (language) => {
    try {
      console.log(`📝 Adding language ${language} to project ${projectId}`);

      const response = await fetch(`${API_URL}/projects/${projectId}/translate?target_language_code=${language}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        console.log(`✅ Language ${language} added successfully`);
        return true;
      } else {
        console.error(`❌ Failed to add language ${language}: ${response.status}`);
        const errorData = await response.json();
        console.error(`Error details:`, errorData);
        return false;
      }
    } catch (error) {
      console.error(`❌ Error adding language ${language}:`, error);
      return false;
    }
  };

  const generateAIScriptForLanguage = async (language) => {
    try {
      // Use the same URL pattern as the manual "AI Gen Script" button
      const url = `${API_URL}/projects/${projectId}/analyze${language && language !== 'original' ? `?target_language_code=${language}` : ''}`;
      console.log(`🤖 Bulk Prepare: Generating AI scripts for ${language} using URL: ${url}`);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        console.log(`✅ AI script generation completed for ${language}`);

        // Refresh timeline items to update UI (same as manual AI Gen Script)
        const timelineResponse = await fetch(`${API_URL}/projects/${projectId}/timeline-items`);
        if (timelineResponse.ok) {
          const timelineData = await timelineResponse.json();
          setTimelineItems(timelineData);
          console.log(`✅ Timeline items refreshed for ${language}`);
        }

        return true;
      } else {
        console.log(`⚠️ AI script generation failed for ${language}, will poll`);
        return false;
      }
    } catch (error) {
      console.error(`❌ Error generating AI script for ${language}:`, error);
      return false;
    }
  };

  const generateVoiceForLanguage = async (language) => {
    try {
      console.log(`🎤 [VOICE GEN] Starting voice generation for ${language} - processing each clip individually`);
      console.log(`🎤 [VOICE GEN] Current timelineItems count:`, timelineItems?.length || 0);

      // Get all timeline items that need voice generation (exclude intro/outro)
      const clipsToProcess = timelineItems.filter(item =>
        item.type !== 'intro' && item.type !== 'outro'
      );

      console.log(`🎤 [VOICE GEN] Clips to process:`, clipsToProcess.length);
      console.log(`🎤 [VOICE GEN] Clip IDs:`, clipsToProcess.map(item => `${item.id}(${item.type})`));

      if (clipsToProcess.length === 0) {
        console.warn(`⚠️ [VOICE GEN] No clips found to process for ${language}`);
        return false;
      }

      let successCount = 0;
      let failureCount = 0;

      for (let i = 0; i < clipsToProcess.length; i++) {
        const item = clipsToProcess[i];

        try {
          console.log(`🎤 [VOICE GEN] Processing clip ${item.id} (${i + 1}/${clipsToProcess.length}) for ${language}`);
          console.log(`🎤 [VOICE GEN] Clip details: type=${item.type}, sort_order=${item.sort_order}`);
          console.log(`🎤 [VOICE GEN] Item data structure:`, {
            id: item.id,
            narration: item.narration ? `"${item.narration.substring(0, 50)}..."` : item.narration,
            scene_narration: item.scene_narration ? `"${item.scene_narration.substring(0, 50)}..."` : item.scene_narration,
            translations: item.translations,
            translationsLength: item.translations?.length || 0,
            availableFields: Object.keys(item)
          });

          // Let backend handle all validation - just pass item_id and language_code
          console.log(`🎤 [VOICE GEN] Sending request to backend for clip ${item.id} in ${language}`);

          // Use individual clip API call like the "Generate Voice" dropdown
          // Using 'eleven_labs' as default voice model (Variation 1)
          const url = `${API_URL}/timeline-items/${item.id}/generate-audio?voice_model=eleven_labs&variation=1${language && language !== 'original' ? `&target_language_code=${language}` : ''}`;
          console.log(`🎤 [VOICE GEN] API URL:`, url);

          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
          });

          console.log(`🎤 [VOICE GEN] Response status for clip ${item.id}:`, response.status);

          if (response.ok) {
            const responseData = await response.text();
            console.log(`🎤 [VOICE GEN] Response data for clip ${item.id}:`, responseData);

            // Check if the response contains an error message
            try {
              const parsedResponse = JSON.parse(responseData);
              if (parsedResponse.message && parsedResponse.message.includes('Failed')) {
                console.error(`❌ [VOICE GEN] Voice generation failed for clip ${item.id}: ${parsedResponse.message}`);
                failureCount++;
              } else {
                console.log(`✅ [VOICE GEN] Voice generated successfully for clip ${item.id}`);
                successCount++;
              }
            } catch (parseError) {
              // If response is not JSON, assume success
              console.log(`✅ [VOICE GEN] Voice generated for clip ${item.id} (non-JSON response)`);
              successCount++;
            }
          } else {
            const errorText = await response.text();
            console.error(`❌ [VOICE GEN] HTTP error for clip ${item.id}: ${response.status} - ${errorText}`);
            failureCount++;
          }

          // Add 2-second delay between each clip generation (as requested)
          if (i < clipsToProcess.length - 1) {
            console.log(`⏳ [VOICE GEN] Waiting 2 seconds before next clip...`);
            await new Promise(resolve => setTimeout(resolve, 2000));
          }

        } catch (error) {
          console.error(`❌ [VOICE GEN] Error generating voice for clip ${item.id}:`, error);
          failureCount++;
        }
      }

      console.log(`✅ [VOICE GEN] Voice generation for ${language} completed: ${successCount} success, ${failureCount} failures`);

      // Refresh timeline items to update UI
      console.log(`🔄 [VOICE GEN] Refreshing timeline items after voice generation...`);
      const timelineResponse = await fetch(`${API_URL}/projects/${projectId}/timeline-items`);
      if (timelineResponse.ok) {
        const timelineData = await timelineResponse.json();
        setTimelineItems(timelineData);
        console.log(`✅ [VOICE GEN] Timeline items refreshed after voice generation for ${language}`);
      } else {
        console.error(`❌ [VOICE GEN] Failed to refresh timeline items:`, timelineResponse.status);
      }

      // Return true if at least some clips succeeded
      const result = successCount > 0;
      console.log(`🎤 [VOICE GEN] Final result for ${language}:`, result ? 'SUCCESS' : 'FAILURE');
      return result;

    } catch (error) {
      console.error(`❌ [VOICE GEN] Critical error generating voice for ${language}:`, error);
      return false;
    }
  };

  const generateConfigurationsForLanguage = async (language) => {
    try {
      console.log(`⚙️ [CONFIG GEN] Starting configuration generation for ${language} - processing each clip individually`);
      console.log(`⚙️ [CONFIG GEN] Current timelineItems count:`, timelineItems?.length || 0);

      // Get all timeline items that need configuration generation (exclude intro/outro)
      const clipsToProcess = timelineItems.filter(item =>
        item.type !== 'intro' && item.type !== 'outro'
      );

      console.log(`⚙️ [CONFIG GEN] Clips to process:`, clipsToProcess.length);
      console.log(`⚙️ [CONFIG GEN] Clip IDs:`, clipsToProcess.map(item => `${item.id}(${item.type})`));

      if (clipsToProcess.length === 0) {
        console.warn(`⚠️ [CONFIG GEN] No clips found to process for ${language}`);
        return false;
      }

      let successCount = 0;
      let failureCount = 0;

      for (let i = 0; i < clipsToProcess.length; i++) {
        const item = clipsToProcess[i];

        try {
          console.log(`⚙️ [CONFIG GEN] Processing clip ${item.id} (${i + 1}/${clipsToProcess.length}) for ${language}`);

          const url = `${API_URL}/timeline-items/${item.id}/generate-config/${language}`;
          console.log(`⚙️ [CONFIG GEN] API URL:`, url);

          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
          });

          console.log(`⚙️ [CONFIG GEN] Response status for clip ${item.id}:`, response.status);

          if (response.ok) {
            console.log(`✅ [CONFIG GEN] Configuration generated for clip ${item.id}`);
            successCount++;
          } else {
            const errorData = await response.json();
            if (errorData.detail && errorData.detail.includes('already exists')) {
              console.log(`ℹ️ [CONFIG GEN] Configuration for clip ${item.id} already exists`);
              successCount++; // Count as success since it already exists
            } else {
              console.error(`❌ [CONFIG GEN] Configuration generation failed for clip ${item.id}: ${errorData.detail || 'Unknown error'}`);
              failureCount++;
            }
          }

          // Add 1-second delay between each clip configuration generation
          if (i < clipsToProcess.length - 1) {
            console.log(`⏳ [CONFIG GEN] Waiting 1 second before next clip...`);
            await new Promise(resolve => setTimeout(resolve, 1000));
          }

        } catch (error) {
          console.error(`❌ [CONFIG GEN] Error generating configuration for clip ${item.id}:`, error);
          failureCount++;
        }
      }

      console.log(`✅ [CONFIG GEN] Configuration generation for ${language} completed: ${successCount} success, ${failureCount} failures`);

      // Return true if at least some clips succeeded
      const result = successCount > 0;
      console.log(`⚙️ [CONFIG GEN] Final result for ${language}:`, result ? 'SUCCESS' : 'FAILURE');
      return result;

    } catch (error) {
      console.error(`❌ [CONFIG GEN] Critical error generating configurations for ${language}:`, error);
      return false;
    }
  };

  const pollForLanguageCompletion = async (language, progressKey) => {
    const maxPollingAttempts = 20; // 20 attempts * 3 minutes = 1 hour max
    let attempts = 0;

    while (attempts < maxPollingAttempts) {
      await new Promise(resolve => setTimeout(resolve, 3 * 60 * 1000)); // Wait 3 minutes
      attempts++;

      try {
        // Check if scripts are available for this language
        const statusResponse = await fetch(`${API_URL}/projects/${projectId}/script-status/${language}`);

        if (statusResponse.ok) {
          const status = await statusResponse.json();
          if (status.hasScripts) {
            console.log(`✅ Polling success: ${language} scripts are now available`);

            // Update progress
            const progressData = JSON.parse(localStorage.getItem(progressKey) || '{}');
            progressData.completedLanguages.push(language);
            localStorage.setItem(progressKey, JSON.stringify(progressData));

            return true;
          }
        }

        console.log(`⏳ Polling attempt ${attempts}: ${language} scripts not ready yet`);
      } catch (error) {
        console.error(`❌ Error polling for ${language}:`, error);
      }
    }

    console.error(`⏰ Polling timeout: ${language} scripts not available after ${maxPollingAttempts} attempts`);
    return false;
  };

  return (
    <div className="video-editor-page-container">
      <header className="video-editor-header">
        <button onClick={() => navigate('/')} className="back-button">← Back to Projects</button>
        <h1>{project ? `Editing: ${project.title}` : 'Video Tutorial Generator'}</h1>
      </header>
      <main className="video-editor-main-content">
        <section className="video-section">
          <div className="video-upload-area">
            {!videoUrl ? (
              <label htmlFor="video-upload" className="file-input-button">
                Upload Video
                <input id="video-upload" type="file" accept="video/*" onChange={handleFileChangeAndSetUrl} style={{ display: 'none' }} />
              </label>
            ) : (
              <div className="video-player-container">
                <div className="video-player-controls-top">
                  <div className="language-selector-container">
                    <div className="language-selector-top">
                      <label htmlFor="choose-language">Language:</label>
                      <div className="custom-select-wrapper">
                        <select
                          id="choose-language"
                          value={selectedLanguage}
                          onChange={(e) => {
                            if (e.target.value === "add-new") {
                              setIsAddLanguageModalOpen(true);
                            } else {
                              setSelectedLanguage(e.target.value);
                            }
                          }}
                          className="custom-select"
                        >
                          <option value="original">Original</option>
                          {Array.isArray(availableLanguages) && availableLanguages.map(lang => (
                            <option key={lang} value={lang}>{languageNames[lang] || lang}</option>
                          ))}
                          <option value="add-new">+ Add New</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="add-video-container">
                    {videos.length > 1 && (
                      <>
                        <button onClick={() => handleVideoToggle('prev')} className="video-toggle-button">
                          ←
                        </button>
                        <span className="video-counter">
                          {currentVideoIndex + 1} / {videos.length}
                        </span>
                        <button onClick={() => handleVideoToggle('next')} className="video-toggle-button">
                          →
                        </button>
                        <span style={{ marginRight: '15px' }}></span>
                      </>
                    )}
                    <button onClick={handleAddVideo} className="add-video-button">
                      Add Video
                    </button>
                  </div>
                  {selectedLanguage !== 'original' && (
                    <button onClick={() => setIsConfirmRemoveOpen(true)} className="reset-video-button">
                      Remove Translation
                    </button>
                  )}
                </div>
                <div className="video-player-wrapper-editor" style={{ position: 'relative' }}>
                  {videoUrl && (
                    <>
                      <video
                        ref={videoRef}
                        width="100%"
                        height="auto"
                        src={videoUrl}
                        className="main-video-player"
                        onTimeUpdate={handleProgress}
                        onLoadedMetadata={handleLoadedMetadata}
                        onPlay={() => setIsPlaying(true)}
                        onPause={() => setIsPlaying(false)}
                        crossOrigin="anonymous"
                      >
                        Your browser does not support the video tag.
                      </video>

                      {/* Video Menu Dropdown */}
                      <div style={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        zIndex: 1000
                      }}>
                        <button
                          onClick={() => setIsVideoMenuOpen(!isVideoMenuOpen)}
                          style={{
                            background: 'rgba(0, 0, 0, 0.7)',
                            border: 'none',
                            borderRadius: '4px',
                            color: 'white',
                            padding: '8px',
                            cursor: 'pointer',
                            fontSize: '16px'
                          }}
                          title="Video options"
                        >
                          ⋮
                        </button>

                        {isVideoMenuOpen && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            right: '0',
                            background: 'white',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                            minWidth: '150px',
                            zIndex: 1001
                          }}>
                            <button
                              onClick={handleReplaceVideo}
                              style={{
                                display: 'block',
                                width: '100%',
                                padding: '12px 16px',
                                border: 'none',
                                background: 'none',
                                textAlign: 'left',
                                cursor: 'pointer',
                                fontSize: '14px',
                                color: '#333'
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            >
                              Replace Video
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
                
                {/* Video Playback Timeline */}
                {videoUrl && (
                  <VideoTimeline
                    videoRef={videoRef}
                    currentTime={currentTime}
                    duration={duration}
                    onSeek={handleSeek}
                    timelineItems={timelineItems}
                  />
                )}
                <div className="player-controls-editor">
                  <p className="current-time-display">Current Time: {currentTime.toFixed(2)}s</p>
                  {!isLocalhost && <button onClick={handleAddFrameToTimeline} className="add-frame-button">Add Frame</button>}
                  {!isLocalhost && <button onClick={handleAddClipToTimeline} className="add-clip-button">{isRecordingClip ? 'Stop Clip' : 'Add Clip'}</button>}
                  {!isLocalhost && <button
                    onClick={() => handleAIGenScript(selectedLanguage)}
                    className={`ai-analyze-button ${!hasDescriptions ? 'disabled' : ''}`}
                    disabled={!hasDescriptions}
                    title={!hasDescriptions ? "Please add descriptions to timeline items before generating AI scripts" : `Generate AI scripts for timeline items in ${getLanguageDisplayText(selectedLanguage)}`}
                  >
                    AI Gen Script ({getLanguageDisplayText(selectedLanguage)})
                  </button>}
                  {!isLocalhost && <button onClick={() => navigate(`/project/${projectId}/youtube`)} className="ai-analyze-button">YouTube Details</button>}
                  {!isLocalhost && <button
                    onClick={() => {
                      console.log('AI Gen Voice button clicked');
                      setIsVoiceModelModalOpen(true);
                    }}
                    className={`ai-analyze-button ${(hasExistingNarrations || hasEmptyNarrations) ? 'disabled' : ''}`}
                    disabled={hasExistingNarrations || hasEmptyNarrations}
                    title={
                      hasExistingNarrations
                        ? `Narrations already exist for ${getLanguageDisplayText(selectedLanguage)}. Remove existing audio first.`
                        : hasEmptyNarrations
                        ? `Some timeline items have empty narration scripts for ${getLanguageDisplayText(selectedLanguage)}. Generate AI scripts first.`
                        : `Generate AI voice narrations in ${getLanguageDisplayText(selectedLanguage)}`
                    }
                  >
                    AI Gen Voice ({getLanguageDisplayText(selectedLanguage)})
                  </button>}
                  {!isLocalhost && <button onClick={handleRemoveAudio} className="remove-vo-button">Remove VO</button>}
                  {!isLocalhost && (
                    <button
                      onClick={() => setIsBulkPrepareModalOpen(true)}
                      className="bulk-prepare-button"
                      title="Prepare AI scripts for multiple languages"
                    >
                      🚀 Bulk Prepare
                    </button>
                  )}
                  {!isLocalhost && <button onClick={handleGenerateTutorial} className="ai-analyze-button">Generate Tutorial</button>}
                  {!isLocalhost && (
                    <button
                      onClick={handleOpenPublishedVideos}
                      className="published-videos-button"
                      style={{
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        padding: '10px 16px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        marginLeft: '8px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#0056b3';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#007bff';
                      }}
                    >
                      📺 Published Videos
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>
        
        <section className="timeline-section-editor">
          <h2>Timeline</h2>
          {timelineItems.length === 0 ? (
            <p className="empty-timeline-message">Add frames or clips to the timeline.</p>
          )
           : (
            <Timeline
              items={timelineItems}
              onDragEnd={handleDragEnd}
              onRemoveItem={handleRemoveItem}
              onUpdateText={handleUpdateText}
              onUpdateClipTimes={handleUpdateClipTimes}
              videoUrl={videoUrl}
              onMoveUp={handleMoveUp}
              onMoveDown={handleMoveDown}
              onUpdateSceneDescription={handleUpdateSceneDescription}
              onUpdateSceneNarration={handleUpdateSceneNarration}
              onRegenerateAudio={handleRegenerateSingleAudio}
              onRegenScript={handleRegenScript}
              setIsLoading={setIsLoading}
              setMessage={setMessage}
              selectedLanguage={selectedLanguage}
              projectId={projectId}
              onQuickPreview={handleQuickPreview}
              onDeploy={() => navigate(`/project/${projectId}/deploy`)}
              onAddIntroOutro={handleAddIntroOutro}
              onDuplicateItem={handleDuplicateItem}
            />
          )}
        </section>
      </main>
      <ClipEditModal
        isOpen={isClipEditModalOpen}
        onClose={() => setIsClipEditModalOpen(false)}
        clip={editingClip}
        videoUrl={getClipVideoUrl(editingClip)}
        onUpdateClipTimes={handleUpdateClipTimes}
      />

      <AddLanguageModal
        isOpen={isAddLanguageModalOpen}
        onClose={() => setIsAddLanguageModalOpen(false)}
        newTranslationLanguage={newTranslationLanguage}
        setNewTranslationLanguage={setNewTranslationLanguage}
        handleTranslate={handleTranslate}
        setIsLoading={setIsLoading}
        setMessage={setMessage}
      />

      <VoiceModelSelectionModal
        isOpen={isVoiceModelModalOpen}
        onClose={() => {
          setIsVoiceModelModalOpen(false);
          setRegeneratingItemId(null); // Reset item ID when closing
          setRegeneratingVariation(1); // Reset variation when closing
        }}
        onSelectModel={(model) => {
          handleAIGenVoice(model, regeneratingItemId, regeneratingVariation); // Pass model, item ID, and variation
          setIsVoiceModelModalOpen(false);
          setRegeneratingItemId(null); // Reset item ID after selection
          setRegeneratingVariation(1); // Reset variation after selection
        }}
      />

      <ConfirmationDialog
        isOpen={isConfirmRemoveOpen}
        title="Confirm Removal"
        message={`Are you sure you want to remove all translation data for '${languageNames[selectedLanguage] || selectedLanguage}'? This action cannot be undone.`}
        onConfirm={handleRemoveTranslation}
        onCancel={() => setIsConfirmRemoveOpen(false)}
      />

      <BulkPrepareModal
        isOpen={isBulkPrepareModalOpen}
        onClose={() => setIsBulkPrepareModalOpen(false)}
        onStartBulkPrepare={handleBulkPrepare}
        availableLanguages={availableLanguages}
      />

      <BulkProgressModal
        isOpen={isBulkProgressModalOpen}
        onClose={() => setIsBulkProgressModalOpen(false)}
        projectId={projectId}
      />

      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Processing... Please wait.</p>
        </div>
      )}

      {message.text && (
        <div className={`message-popup ${message.type}`}>
          <p>{message.text}</p>
          <button onClick={() => setMessage({ text: '', type: '' })}>Close</button>
        </div>
      )}
    </div>
  );
}

export default VideoEditorPage;